import React, {
  useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback,
} from "react";

/**
 * The date field, ours rather than the browser's.
 *
 * `input[type="date"]` paints a calendar the theme cannot reach: Chrome's popup
 * is a native widget, so it stayed white-on-white with a #1a73e8 selection in
 * dark mode and ignored every token in the sheet. This is the same control
 * drawn in DOM — a recessed well, a floating panel, the market accent on the
 * selected day — so it answers `data-theme` like everything else.
 *
 * The value contract is unchanged from the input it replaces: an ISO
 * `yyyy-mm-dd` string, or `''` for empty, with `min`/`max` as the same. Only
 * the *display* is dd/mm/yyyy, which is what the native field was showing.
 */

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/* Dates are handled as local Y/M/D triples throughout, never as `Date`
   instants. `new Date('2026-08-28')` parses as UTC midnight, so west of
   Greenwich it renders as the 27th — the classic off-by-one that makes a
   picker select the day before the one clicked. */
const toISO = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const parseISO = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  if (!m) return null;
  return { y: +m[1], m: +m[2] - 1, d: +m[3] };
};

const formatDisplay = (s) => {
  const p = parseISO(s);
  return p ? `${String(p.d).padStart(2, "0")}/${String(p.m + 1).padStart(2, "0")}/${p.y}` : "";
};

const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"
       stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

const Chevron = ({ dir }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       style={{ transform: dir === "left" ? "rotate(180deg)" : undefined }}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const DateField = ({
  id,
  value,
  onChange,
  min,
  max,
  placeholder = "dd/mm/yyyy",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [pickingMonth, setPickingMonth] = useState(false);
  /* Where the panel hangs, resolved by measurement rather than assumed. The
     field lives inside `react-responsive-modal`, which sets `overflow-y: auto`
     on its shell — that computes overflow-x to `auto` as well, so a panel
     hanging off the right-hand "To Date" column was sliced at the modal's
     edge rather than spilling over it. */
  const [place, setPlace] = useState({ align: "left", flip: false });
  const wrapRef = useRef(null);
  const fieldRef = useRef(null);
  const panelRef = useRef(null);

  const selected = useMemo(() => parseISO(value), [value]);
  const today = useMemo(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() };
  }, []);

  /* The month on screen. Follows the selection when there is one, so reopening
     a filled field lands you on the day you chose rather than on today. */
  const [view, setView] = useState(() => ({
    y: (selected || today).y,
    m: (selected || today).m,
  }));

  useEffect(() => {
    if (!open) return;
    const base = parseISO(value) || today;
    setView({ y: base.y, m: base.m });
    setPickingMonth(false);
  }, [open, value, today]);

  /* The box the panel has to stay inside: the nearest ancestor that clips,
     or the viewport when nothing does. */
  const clipRect = () => {
    let el = wrapRef.current?.parentElement;
    while (el && el !== document.body) {
      const { overflow, overflowX, overflowY } = window.getComputedStyle(el);
      if (`${overflow}${overflowX}${overflowY}`.match(/auto|scroll|hidden/)) {
        return el.getBoundingClientRect();
      }
      el = el.parentElement;
    }
    return { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
  };

  /* Measured before paint, so the panel does not appear in one place and jump
     to another. Re-measured when the month/year sheet swaps in, because that
     view is a different height. */
  useLayoutEffect(() => {
    if (!open || !panelRef.current || !fieldRef.current) return;
    const panel = panelRef.current.getBoundingClientRect();
    const field = fieldRef.current.getBoundingClientRect();
    const clip = clipRect();
    const M = 8;
    setPlace({
      align: field.left + panel.width > clip.right - M && field.right - panel.width > clip.left + M
        ? "right"
        : "left",
      flip: field.bottom + panel.height > clip.bottom - M && field.top - panel.height > clip.top + M,
    });
  }, [open, pickingMonth]);

  // Close on outside pointer or Esc — a popover that only closes on re-click
  // strands itself over the rest of the form.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        fieldRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const disabled = useCallback(
    (iso) => (min && iso < min) || (max && iso > max),
    [min, max]
  );

  const pick = (iso) => {
    if (disabled(iso)) return;
    onChange(iso);
    setOpen(false);
  };

  const step = (delta) => {
    setView((v) => {
      const m = v.m + delta;
      return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 };
    });
  };

  /* Six weeks, always — a grid that grows a row between March and May makes
     the panel jump height as you page through it. */
  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1).getDay();
    const out = [];
    for (let i = 0; i < 42; i += 1) {
      const dayNum = i - first + 1;
      const date = new Date(view.y, view.m, dayNum);
      out.push({
        y: date.getFullYear(),
        m: date.getMonth(),
        d: date.getDate(),
        outside: dayNum < 1 || dayNum > daysInMonth(view.y, view.m),
      });
    }
    return out;
  }, [view]);

  const navBtn = {
    width: 28,
    height: 28,
    display: "grid",
    placeItems: "center",
    borderRadius: "var(--r-pill)",
    background: "var(--raise)",
    boxShadow: "var(--pillow-sm)",
    color: "var(--ink-2)",
    cursor: "pointer",
    flexShrink: 0,
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        id={id}
        ref={fieldRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="ce-field ce-square w-full px-4 py-2.5 text-sm flex items-center justify-between gap-2"
        style={{
          color: value ? "var(--ink)" : "var(--ink-3)",
          textAlign: "left",
          cursor: "pointer",
          // The field is a well, and the sweep in §7 turns every button into a
          // pill — `ce-square` holds it to the field radius instead.
          boxShadow: open ? "var(--inset-sm), 0 0 0 2px var(--cat-accent)" : undefined,
        }}
      >
        <span>{formatDisplay(value) || placeholder}</span>
        <span style={{ color: "var(--ink-3)", display: "flex" }}><CalendarIcon /></span>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Choose date"
          style={{
            position: "absolute",
            zIndex: 60,
            [place.align]: 0,
            width: 268,
            [place.flip ? "bottom" : "top"]: "calc(100% + 8px)",
            background: "var(--surface)",
            borderRadius: "var(--r-panel)",
            boxShadow: "var(--float)",
            padding: 12,
            animation: "ce-modal-in var(--dur-fast) var(--ease) both",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <button
              type="button"
              onClick={() => setPickingMonth((p) => !p)}
              className="ce-square"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 8px",
                borderRadius: "var(--r-control)",
                background: "transparent",
                color: "var(--ink)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {MONTHS[view.m]} {view.y}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                   style={{ transform: pickingMonth ? "rotate(180deg)" : undefined }}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            <div className="flex items-center gap-1.5">
              <button type="button" aria-label="Previous month" onClick={() => step(-1)} style={navBtn}>
                <Chevron dir="left" />
              </button>
              <button type="button" aria-label="Next month" onClick={() => step(1)} style={navBtn}>
                <Chevron dir="right" />
              </button>
            </div>
          </div>

          {pickingMonth ? (
            <div>
              {/* Year stepper, then the twelve months. Two taps to anywhere,
                  instead of paging a month at a time to last January. */}
              <div className="flex items-center justify-between mb-2" style={{ padding: "0 2px" }}>
                <button type="button" aria-label="Previous year"
                        onClick={() => setView((v) => ({ ...v, y: v.y - 1 }))} style={navBtn}>
                  <Chevron dir="left" />
                </button>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{view.y}</span>
                <button type="button" aria-label="Next year"
                        onClick={() => setView((v) => ({ ...v, y: v.y + 1 }))} style={navBtn}>
                  <Chevron dir="right" />
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                {MONTHS.map((name, i) => {
                  const active = i === view.m;
                  return (
                    <button
                      key={name}
                      type="button"
                      className="ce-square"
                      onClick={() => {
                        setView((v) => ({ ...v, m: i }));
                        setPickingMonth(false);
                      }}
                      style={{
                        height: 32,
                        fontSize: 12,
                        fontWeight: active ? 600 : 500,
                        borderRadius: "var(--r-control)",
                        cursor: "pointer",
                        background: active ? "var(--cat-accent)" : "var(--surface-2)",
                        boxShadow: active ? "none" : "var(--inset-sm)",
                        color: active ? "#fff" : "var(--ink-2)",
                      }}
                    >
                      {name.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
                {WEEKDAYS.map((w, i) => (
                  <span key={`${w}${i}`} className="ce-eyebrow" style={{ textAlign: "center", lineHeight: "22px" }}>
                    {w}
                  </span>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                {cells.map((c) => {
                  const iso = toISO(c.y, c.m, c.d);
                  const isSel = !!selected && selected.y === c.y && selected.m === c.m && selected.d === c.d;
                  const isToday = c.y === today.y && c.m === today.m && c.d === today.d;
                  const off = disabled(iso);
                  return (
                    <button
                      key={iso}
                      type="button"
                      className="ce-square"
                      disabled={off}
                      onClick={() => pick(iso)}
                      aria-current={isToday ? "date" : undefined}
                      style={{
                        height: 32,
                        fontSize: 12.5,
                        fontWeight: isSel || isToday ? 600 : 500,
                        borderRadius: "var(--r-control)",
                        cursor: off ? "not-allowed" : "pointer",
                        // Outside-month days stay visible but recede; disabled
                        // ones fade further, so "not this month" and "not
                        // allowed" read as different states.
                        opacity: off ? 0.3 : c.outside ? 0.42 : 1,
                        background: isSel ? "var(--cat-accent)" : "transparent",
                        boxShadow: isSel
                          ? "none"
                          : isToday
                          ? "inset 0 0 0 1.5px var(--cat-accent-line)"
                          : "none",
                        color: isSel ? "#fff" : isToday ? "var(--cat-accent)" : "var(--ink-2)",
                        transition: "background var(--dur-fast) var(--ease)",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSel && !off) e.currentTarget.style.background = "var(--cat-accent-soft)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSel) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      {c.d}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: "1px solid var(--line)" }}>
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              style={{ background: "transparent", fontSize: 12, fontWeight: 600, color: "var(--ink-3)", cursor: "pointer", padding: "4px 6px" }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => pick(toISO(today.y, today.m, today.d))}
              disabled={disabled(toISO(today.y, today.m, today.d))}
              style={{ background: "transparent", fontSize: 12, fontWeight: 600, color: "var(--cat-accent)", cursor: "pointer", padding: "4px 6px" }}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateField;
